import { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import DealCard from './DealCard';

function StageColumn({ stage, deals, onDealClick }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `stage-${stage.id}`,
    data: { type: 'stage', stageId: stage.id },
  });

  const totalValue = deals.reduce((sum, d) => sum + (d.value || 0), 0);
  const formattedTotal = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(totalValue);

  return (
    <div
      className="flex flex-col min-w-[280px] max-w-[320px] flex-shrink-0"
    >
      {/* Column Header */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: stage.color }}
        />
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {stage.name}
        </h3>
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto flex-shrink-0">
          {deals.length}
        </span>
      </div>

      {/* Total Value */}
      {totalValue > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 px-1">
          {formattedTotal}
        </div>
      )}

      {/* Cards Container */}
      <div
        ref={setNodeRef}
        className={`flex-1 space-y-2 p-2 rounded-lg min-h-[100px] transition-colors ${
          isOver
            ? 'bg-brand-50 dark:bg-brand-900/10 ring-2 ring-brand-300 dark:ring-brand-700'
            : 'bg-gray-50 dark:bg-gray-800/50'
        }`}
      >
        <SortableContext
          items={deals.map((d) => d.id)}
          strategy={verticalListSortingStrategy}
        >
          {deals.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              onClick={() => onDealClick?.(deal)}
            />
          ))}
        </SortableContext>

        {deals.length === 0 && (
          <div className="flex items-center justify-center h-20 text-xs text-gray-400 dark:text-gray-500">
            No deals
          </div>
        )}
      </div>
    </div>
  );
}

export default function PipelineBoard({ stages, deals, onMoveDeal, onDealClick }) {
  const [activeDeal, setActiveDeal] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor)
  );

  // Group deals by stage
  const dealsByStage = useMemo(() => {
    const grouped = {};
    for (const stage of stages) {
      grouped[stage.id] = [];
    }
    for (const deal of deals) {
      if (grouped[deal.stage_id]) {
        grouped[deal.stage_id].push(deal);
      }
    }
    return grouped;
  }, [stages, deals]);

  const handleDragStart = useCallback((event) => {
    const deal = deals.find((d) => d.id === event.active.id);
    setActiveDeal(deal || null);
  }, [deals]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveDeal(null);

    if (!over) return;

    const dealId = active.id;
    let targetStageId = null;

    // Check if dropped on a stage column
    if (over.id.toString().startsWith('stage-')) {
      targetStageId = over.id.toString().replace('stage-', '');
    } else {
      // Dropped on another deal card — find its stage
      const targetDeal = deals.find((d) => d.id === over.id);
      if (targetDeal) {
        targetStageId = targetDeal.stage_id;
      }
    }

    if (targetStageId) {
      const deal = deals.find((d) => d.id === dealId);
      if (deal && deal.stage_id !== targetStageId) {
        onMoveDeal?.(dealId, targetStageId);
      }
    }
  }, [deals, onMoveDeal]);

  const sortedStages = useMemo(
    () => [...stages].sort((a, b) => a.position - b.position),
    [stages]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
        {sortedStages.map((stage) => (
          <StageColumn
            key={stage.id}
            stage={stage}
            deals={dealsByStage[stage.id] || []}
            onDealClick={onDealClick}
          />
        ))}
      </div>

      <DragOverlay>
        {activeDeal ? (
          <DealCard deal={activeDeal} isDragOverlay />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
